import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { AvatarFrame } from '@/components/profile/AvatarFrame';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useEquipFrame, usePurchaseItem, useStore } from '@/features/games/store';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { StoreItem } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

function FrameCard({ item, balance, featured }: { item: StoreItem; balance: number; featured?: boolean }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const toast = useToast();
  const purchase = usePurchaseItem();
  const equip = useEquipFrame();
  const busy = purchase.isPending || equip.isPending;
  const canAfford = balance >= item.cost_coins;

  const onBuy = () =>
    purchase.mutate(item.key, {
      onSuccess: () => toast.show({ message: t('store.bought'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  const onEquip = () =>
    equip.mutate(item.equipped ? null : item.key, {
      onSuccess: () =>
        toast.show({ message: item.equipped ? t('store.removed') : t('store.equipDone'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });

  const avatarSize = featured ? 68 : 52;

  return (
    <Card
      className={`mb-3 items-center px-3 py-4 ${featured ? 'w-full py-5' : ''}`}
      style={
        featured
          ? { borderColor: palette.captain + '55', borderWidth: 1, backgroundColor: palette.captain + '0F' }
          : { width: '47.5%' }
      }
    >
      {featured ? (
        <View
          className="mb-3 flex-row items-center gap-1 rounded-full px-2.5 py-1"
          style={{ backgroundColor: palette.captain + '22' }}
        >
          <Ionicons name="star" size={11} color={palette.captain} />
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide" style={{ color: palette.captain }}>
            {t('store.featured')}
          </AppText>
        </View>
      ) : null}
      <AvatarFrame frameKey={item.key} size={avatarSize}>
        <View
          className="items-center justify-center rounded-full"
          style={{ width: avatarSize, height: avatarSize, backgroundColor: palette.inset }}
        >
          <Ionicons name="person" size={featured ? 34 : 26} color={palette.muted} />
        </View>
      </AvatarFrame>
      <AppText variant="caption" weight="bold" className="mt-3 text-center text-ink" numberOfLines={1}>
        {t(item.label_key)}
      </AppText>
      {item.owned ? (
        <View className={`mt-2 ${featured ? 'w-1/2' : 'w-full'}`}>
          {item.equipped ? (
            <View className="flex-row items-center justify-center gap-1 py-1.5">
              <Ionicons name="checkmark-circle" size={14} color={palette.win} />
              <AppText variant="micro" weight="bold" style={{ color: palette.win }}>
                {t('store.ownedTag')}
              </AppText>
            </View>
          ) : null}
          <Button
            label={item.equipped ? t('store.equipped') : t('store.equip')}
            variant={item.equipped ? 'secondary' : 'primary'}
            compact
            loading={busy}
            onPress={onEquip}
          />
        </View>
      ) : (
        <View className={`mt-2 ${featured ? 'w-1/2' : 'w-full'}`}>
          <Button
            label={`${formatNumber(item.cost_coins, numeralOptions)}`}
            variant="primary"
            compact
            disabled={!canAfford}
            loading={busy}
            onPress={onBuy}
          />
        </View>
      )}
    </Card>
  );
}

export default function StoreScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const { userId } = useAuth();
  const router = useRouter();
  const howto = useHowToPlay('store');
  const store = useStore();

  const balance = store.data?.balance ?? 0;

  const frames = useMemo(
    () => (store.data?.items ?? []).filter((it) => it.kind === 'avatar_frame'),
    [store.data],
  );
  // Spotlight one item: the priciest still-unowned frame, else the priciest overall.
  const featured = useMemo(() => {
    if (frames.length === 0) return null;
    const pool = frames.some((f) => !f.owned) ? frames.filter((f) => !f.owned) : frames;
    return pool.reduce((best, f) => (f.cost_coins > best.cost_coins ? f : best), pool[0]);
  }, [frames]);
  const rest = useMemo(
    () => frames.filter((f) => f.key !== featured?.key),
    [frames, featured],
  );

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="store" icon="bag-handle" />
      <BackHeader title={t('store.title')} right={<HowToPlayButton onPress={howto.open} />} />
      <ScrollView contentContainerStyle={{ paddingBottom: 36 }}>
        {!userId ? (
          <EmptyState icon="bag-handle-outline" title={t('rewards.signInTitle')} body={t('predictions.signInToPlay')} />
        ) : (
          <>
            {/* Balance header */}
            <Card className="mx-4 mb-3 mt-1 flex-row items-center gap-3 px-4 py-3.5">
              <View
                className="h-11 w-11 items-center justify-center rounded-full"
                style={{ backgroundColor: palette.voltGlassStrong }}
              >
                <Ionicons name="server" size={22} color={palette.voltBright} />
              </View>
              <View className="flex-1">
                <AppText variant="micro" className="uppercase tracking-wide text-muted">
                  {t('store.balanceLabel')}
                </AppText>
                <View className="flex-row items-baseline gap-1.5">
                  <AppText variant="title" weight="bold" tabular className="text-volt-bright">
                    {formatNumber(balance, numeralOptions)}
                  </AppText>
                  <AppText variant="caption" className="text-muted">
                    {t('store.balance')}
                  </AppText>
                </View>
              </View>
            </Card>

            {/* Earn-more-coins hint -> Rewards (daily missions + watch-ad) */}
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/rewards' as never)}
              className="mx-4 mb-4 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3 active:opacity-80"
            >
              <Ionicons name="add-circle" size={20} color={palette.win} />
              <View className="flex-1">
                <AppText variant="caption" weight="bold" className="text-ink">
                  {t('store.earnTitle')}
                </AppText>
                <AppText variant="micro" className="text-muted">
                  {t('store.earnHint')}
                </AppText>
              </View>
              <Ionicons name="chevron-forward" size={16} color={palette.muted} />
            </Pressable>

            <AppText variant="caption" weight="bold" className="mx-4 mb-2 uppercase tracking-wide text-soft">
              {t('store.framesSection')}
            </AppText>
            {store.isError ? (
              <ErrorState error={store.error} onRetry={() => store.refetch()} />
            ) : store.isPending ? (
              <View className="flex-row flex-wrap justify-between px-4">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="mb-3 h-36 w-[47.5%]" />
                ))}
              </View>
            ) : (
              <>
                {featured ? (
                  <View className="px-4">
                    <FrameCard item={featured} balance={balance} featured />
                  </View>
                ) : null}
                <View className="flex-row flex-wrap justify-between px-4">
                  {rest.map((item) => (
                    <FrameCard key={item.key} item={item} balance={balance} />
                  ))}
                </View>
              </>
            )}

            <AppText variant="micro" className="mx-4 mt-2 text-center text-muted">
              {t('store.hint')}
            </AppText>
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
